import { dedupeItinerarySlotsByPlace } from '@/utils/postLocationStopIdentity';
import { Location as LegacyLocation } from '@/models/locationModel';
import type { PostLocationExtended } from "@/components/Map/PostLocationMap";
import type {
  PostPresentationActivity,
  PostPresentationAuthor,
  PostPresentationData,
  PostPresentationDay,
  PostPresentationMetadata,
  PostTypeValue,
} from "@/components/blocks/PostPresentation/types";
import type {
  RawItineraryPost,
  RawItineraryPostLocation,
  RawItineraryDayInfo,
  RawLocationData,
} from "@/utils/itinerary/types";
import Post from '@/models/post';
import { PostType } from "@/models/post";
import { debug } from '@/utils/logger';
import { stripDaySuffix } from '@/utils/locationDataExtractor';
import { isUnscheduledDayIndex } from '@/utils/itinerary/unscheduled';
import { getPrimaryDestinationCoordinates } from '@/utils/cityCoordinates';
import { getCityDisplayText } from '@/utils/cityUtils';
import { getPlaceSummaryFromLocation } from '@/utils/googlePlaceOverview';
import { getCityDisplayNameFromPost } from '@/utils/cityExtractionFromPost';
import { getGooglePhotoProxyUrl, getGooglePhotoProxyUrlFromName } from '@/utils/googlePhotoProxy';
import { getFirstPhotoUrl, extractPhotosFromLocation, isDirectGooglePhotoUrl } from '@/utils/photoUtils';

interface NormalizeOptions {
  currentUserId?: string;
}

/**
 * Returns the best human-readable location label for a post card.
 *
 * For Place posts, uses getCityDisplayText on the linked location document to
 * strip street details and return "City, State, Country" format — matching
 * how Lists and Itineraries display via city_identifying_names.
 *
 * Fallback chain for Place posts:
 *   getCityDisplayText(location)  →  strips street, returns "City, State, Country"
 *   → post.city_identifying_names[0]
 *   → cities[0].short_name / long_name
 *   → location.name
 *   → 'Unknown Location'
 */
export function getPostDestinationLabel(post: {
  post_type?: string | null;
  postLocations?: Array<{
    name?: string;
    location?: {
      name?: string;
      a3_intl_address?: string;
    } | null;
  }> | null;
  post_locations?: Array<{
    name?: string;
    location?: {
      name?: string;
      a3_intl_address?: string;
    } | null;
  }> | null;
  city_identifying_names?: string[] | null;
  cities?: Array<{ short_name?: string; long_name?: string }> | null;
  name?: string | null;
  primary_destination?: { identifying_name?: string } | null;
}): string {
  // primary_destination is the source of truth when set
  if (post.primary_destination?.identifying_name) {
    return post.primary_destination.identifying_name;
  }

  const locations = post.postLocations ?? post.post_locations;

  if (post.post_type === 'Place') {
    const loc = locations?.[0]?.location;
    const cityText = loc ? getCityDisplayText(loc) : undefined;
    const placeLabel =
      cityText ||
      post.city_identifying_names?.[0] ||
      post.cities?.[0]?.short_name ||
      post.cities?.[0]?.long_name ||
      loc?.name;
    if (placeLabel) return placeLabel;
  } else {
    // For List / Itinerary: city_identifying_names is the primary source
    const listOrItineraryLabel =
      post.city_identifying_names?.[0] ||
      post.cities?.[0]?.short_name ||
      post.cities?.[0]?.long_name;
    if (listOrItineraryLabel) return listOrItineraryLabel;
  }

  // Fallback: same logic as inspo-to-trip (user_defined_destinations, post_locations, etc.)
  // so posts without primary_destination or city_identifying_names still show a location
  const extracted = getCityDisplayNameFromPost(post as Post);
  return extracted || 'Unknown Location';
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const safeNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  if (isRecord(value) && "$numberDouble" in value) {
    const nested = Number(value["$numberDouble"]);
    return Number.isFinite(nested) ? nested : undefined;
  }
  return undefined;
};

const extractCoordinates = (
  location: RawLocationData | undefined
): [number, number] | null => {
  if (!location) {
    return null;
  }
  if (location.geoposition?.coordinates?.length === 2) {
    const [lng, lat] = location.geoposition.coordinates;
    return [Number(lng), Number(lat)];
  }

  const lat =
    safeNumber(location.google_places_data?.location?.latitude) ??
    safeNumber((location as { location?: { latitude?: number } }).location?.latitude);
  const lng =
    safeNumber(location.google_places_data?.location?.longitude) ??
    safeNumber((location as { location?: { longitude?: number } }).location?.longitude);

  if (lat === undefined || lng === undefined) {
    return null;
  }

  return [lng, lat];
};

const buildActivity = (
  loc: RawItineraryPostLocation
): PostPresentationActivity => {
  // Extract coordinates BEFORE sanitization (needed for map pins)
  const coordinates = extractCoordinates(loc.location);
  
  // Extract photos BEFORE sanitization (sanitizeData removes google_places_data)
  const originalLocation = loc.location || {};
  
  // Now sanitize location data (removes google_places_data but preserves photos if they exist)
  const locationData = LegacyLocation.sanitizeData(
    originalLocation as Partial<LegacyLocation> & Record<string, unknown>
  );
  
  
  // Extract photos - match old working behavior: check locationData.photos first (from Location class)
  // If locationData.photos exists, it means Location.extractFromGooglePlacesData already processed them
  const existingPhotos = locationData.photos || [];
  // Never use direct Google photo URLs from API (backend sends identifiers only with photoFormat=identifiers; filter defensively)
  const toUrl = (u: unknown): string => (typeof u === 'string' ? u : (u as { url?: string })?.url ?? '');
  const photosFromImageUrls = loc.image_urls && loc.image_urls.length > 0
    ? loc.image_urls.map(toUrl).filter((url) => url.length > 0 && !isDirectGooglePhotoUrl(url))
    : [];
  
  // Check loc.location.image_urls if loc.image_urls is empty
  let locationImageUrls: string[] = [];
  if (!photosFromImageUrls.length && loc.location?.image_urls && Array.isArray(loc.location.image_urls) && loc.location.image_urls.length > 0) {
    locationImageUrls = loc.location.image_urls.map(toUrl).filter((url) => url.length > 0 && !isDirectGooglePhotoUrl(url));
  }
  
  // User-uploaded photos (S3) take precedence over Google photos
  // Priority: image_urls (user uploaded S3) > locationData (Google photos) > location.image_urls
  let imageUrl: string | null = null;
  
  // First priority: User-uploaded photos from S3 (image_urls)
  if (photosFromImageUrls.length > 0) {
    imageUrl = photosFromImageUrls[0];
  }
  
  // Second priority: Google photos from locationData
  if (!imageUrl && locationData && (locationData.photos || locationData.google_places_data)) {
    const extractedUrl = getFirstPhotoUrl(locationData as Record<string, unknown>, 400);
    if (extractedUrl) {
      imageUrl = extractedUrl;
    }
  }
  
  // Third priority: location.image_urls (fallback)
  if (!imageUrl && locationImageUrls.length > 0) {
    imageUrl = locationImageUrls[0];
  }
  
  // Extract ALL photo URLs for carousel display
  // User-uploaded photos (S3) take precedence and appear first
  const allPhotoUrls: string[] = [];
  
  // 1. First: User-uploaded photos from S3 (highest priority)
  allPhotoUrls.push(...photosFromImageUrls);
  
  // 2. Second: Google photos from locationData
  if (locationData && (locationData.photos || locationData.google_places_data)) {
    const extractedPhotos = extractPhotosFromLocation(locationData as Record<string, unknown>, 400);
    allPhotoUrls.push(...extractedPhotos);
  }
  
  // 3. Third: location.image_urls (fallback)
  if (!photosFromImageUrls.length) {
    allPhotoUrls.push(...locationImageUrls);
  }
  
  // Remove duplicates, filter out empty strings, and never use direct Google photo URLs
  const uniquePhotoUrls = Array.from(
    new Set(allPhotoUrls.filter((url): url is string => Boolean(url) && !isDirectGooglePhotoUrl(url)))
  );
  
  // Ensure photos array is preserved on locationData (needed for display)
  // Only preserve existingPhotos (identifier objects) - no legacy format
  if (existingPhotos.length > 0) {
    (locationData as Record<string, unknown>).photos = existingPhotos;
  }
  
  // Restore google_places_data after sanitization (needed for map rendering and photo fallbacks)
  // This was removed by sanitizeData but is needed for map pins and photo fallbacks
  if (originalLocation?.google_places_data) {
    locationData.google_places_data = originalLocation.google_places_data as typeof locationData.google_places_data;
  }
  
  // Ensure geoposition is preserved (needed for map pins)
  // sanitizeData preserves it via spread, but explicitly ensure it exists
  if (originalLocation?.geoposition) {
    (locationData as { geoposition?: { type?: string; coordinates?: [number, number] } }).geoposition = originalLocation.geoposition as { type?: string; coordinates?: [number, number] };
  } else if (coordinates) {
    // If geoposition doesn't exist but we have coordinates, create it
    (locationData as { geoposition?: { type: string; coordinates: [number, number] } }).geoposition = {
      type: "Point",
      coordinates: [coordinates[0], coordinates[1]], // [lng, lat]
    };
  }
  // Calculate recommendations count (rec_count from location data)
  // According to backend docs (HOW_TO_GET_RECOMMENDATION_COUNTS.md), GET /api/v1/post/:postId and
  // GET /api/v2/post/:postId now include rec_count in postLocation.location.rec_count for each location.
  // This is automatically calculated via batch aggregation queries.
  // Priority: loc.location.rec_count (from API) > locationData.rec_count > recommendation_count > recommendations array length
  let recommendationsCount = 0;
  let recommendationsSource = 'none';
  
  // First check loc.location.rec_count (most reliable, comes from API on Location object)
  // According to backend docs, this is now included in post endpoints at postLocation.location.rec_count
  // Support both snake_case and camelCase from backend
  // RawLocationData has [key: string]: unknown so we access via index signature
  const locLocationRecCount = loc.location?.['rec_count'];
  const locLocationRecCountCamel = loc.location?.['recCount'];
  const locRecCount = (loc as Record<string, unknown>)['rec_count'];
  if (typeof locLocationRecCount === "number" && locLocationRecCount > 0) {
    recommendationsCount = locLocationRecCount;
    recommendationsSource = 'loc.location.rec_count';
  } else if (typeof locLocationRecCountCamel === "number" && locLocationRecCountCamel > 0) {
    recommendationsCount = locLocationRecCountCamel;
    recommendationsSource = 'loc.location.recCount';
  } else if (typeof locationData.rec_count === "number" && locationData.rec_count > 0) {
    recommendationsCount = locationData.rec_count;
    recommendationsSource = 'locationData.rec_count';
  } else if (typeof locRecCount === "number" && locRecCount > 0) {
    recommendationsCount = locRecCount;
    recommendationsSource = 'loc.rec_count';
  } else if (typeof loc.recommendation_count === "number" && loc.recommendation_count > 0) {
    recommendationsCount = loc.recommendation_count;
    recommendationsSource = 'loc.recommendation_count';
  } else if (Array.isArray(loc.location?.recommendations) && loc.location.recommendations.length > 0) {
    recommendationsCount = loc.location.recommendations.length;
    recommendationsSource = 'loc.location.recommendations.length';
  } else if (Array.isArray(loc.recommendations) && loc.recommendations.length > 0) {
    recommendationsCount = loc.recommendations.length;
    recommendationsSource = 'loc.recommendations.length';
  } else if (Array.isArray(locationData.recommendations) && locationData.recommendations.length > 0) {
    recommendationsCount = locationData.recommendations.length;
    recommendationsSource = 'locationData.recommendations.length';
  }

  const travellerTypes =
    locationData.google_types?.slice?.(0, 4) ??
    (Array.isArray((loc as { traveller_types?: string[] }).traveller_types)
      ? (loc as { traveller_types?: string[] }).traveller_types
      : loc.location?.google_types);

  const timeRange =
    loc.time ||
    loc.timeRange ||
    loc.time_range ||
    loc.location?.timeRange ||
    loc.location?.time ||
    undefined;

  const costValue =
    loc.budget ??
    loc.cost ??
    loc.location?.budget ??
    loc.location?.cost ??
    null;

  // SLOT TYPE RESOLUTION - with detailed logging
  const resolvedSlotType =
    loc.gt_category ||
    locationData.gt_category ||
    loc.slotType ||
    "do";

  // For user-created places, prioritize body for title and address field for address
  // For Google Places, use location.name and Google Places formatted address
  // Note: is_user_created is a PostLocation property, not a Location property.
  // Mirror the heuristic from initializeItinerarySlotsFromPost: treat as user-created
  // if the flag is set OR if there is no linked location (no _id and no google_place_id).
  // The backend's itinerary postLocations path never persisted is_user_created, so DB
  // rows may have false even for genuinely user-created stops.
  const hasNoLinkedLocation = !loc.location?._id && !loc.location?.google_place_id;
  const isUserCreated = loc.is_user_created || loc.isUserCreated || hasNoLinkedLocation;
  
  
  const cleanedBody = stripDaySuffix(loc.body);
  // For user-created places: prefer the explicit `name` field (stored by itinerary_slots path,
  // distinct from body/notes). Fall back to cleanedBody only when name is absent — this avoids
  // the old postLocations-path bug where body was set equal to notes.
  const explicitName = (loc as { name?: string }).name;
  
  const displayTitle = isUserCreated
    ? (explicitName || cleanedBody || "Untitled stop")
    : (loc.location?.name || cleanedBody || explicitName || "Untitled stop");
  
  // Log recommendations calculation (after displayTitle is defined)
  try {
    // Compact numeric-focused log for rec_count visibility
    debug('[postPresentationNormalizer] rec_count trace', {
      placeTitle: displayTitle || loc.body || 'Unknown',
      locLocationRecCount: loc.location?.['rec_count'],
      locLocationRecCountCamel: loc.location?.['recCount'],
      locationDataRecCount: locationData.rec_count,
      locRecCount: (loc as Record<string, unknown>)['rec_count'],
      final: recommendationsCount,
      source: recommendationsSource,
    });
    // More detailed logging to debug rec_count location
    const locLocationKeys = loc.location ? Object.keys(loc.location) : [];
    debug('[postPresentationNormalizer] Recommendations calculation', {
      placeTitle: displayTitle || loc.body || 'Unknown',
      placeId: loc._id || loc.location_id || 'Unknown',
      finalRecommendations: recommendationsCount,
      source: recommendationsSource,
      hasLocLocation: !!loc.location,
      locLocationKeys: locLocationKeys.slice(0, 10), // First 10 keys
      hasLocLocationRecCount: typeof loc.location?.rec_count === 'number',
      locLocationRecCountValue: loc.location?.rec_count,
      hasLocationDataRecCount: typeof locationData.rec_count === 'number',
      locationDataRecCountValue: locationData.rec_count,
      hasLocRecCount: typeof (loc as Record<string, unknown>)['rec_count'] === 'number',
      locRecCountValue: (loc as Record<string, unknown>)['rec_count'],
      hasLocRecommendations: Array.isArray(loc.recommendations),
      hasLocationDataRecommendations: Array.isArray(locationData.recommendations),
      // Check if rec_count might be nested elsewhere
      hasLocLocationRecReferences: Array.isArray(loc.location?.['rec_references']),
    });
  } catch (logError) {
    // Don't let logging errors break the normalization
    debug('[postPresentationNormalizer] Error in recommendations logging:', logError);
  }
  
  const displayAddress = isUserCreated
    ? ((loc as { address?: string }).address || loc.location?.a3_intl_address || loc.location?.address || locationData.address || locationData.a3_intl_address)
    : ((loc as { address?: string }).address || 
       loc.location?.google_places_data?.formattedAddress ||
       loc.location?.a3_intl_address ||
       loc.location?.address ||
       locationData.a3_intl_address ||
       locationData.address);
  
  
  // Use notes directly — never fall back to body. body may equal notes for old user-created
  // stops saved via the postLocations path; conflating them would show notes as the title.
  const displayNotes = (typeof loc.notes === 'string' && loc.notes.trim().length > 0)
    ? loc.notes
    : undefined;
  
  // Log computed displayTitle and displayAddress for user-created places
  if (isUserCreated) {
    debug('[postPresentationNormalizer] buildActivity - DISPLAY VALUES COMPUTED', {
      postLocationId: loc._id,
      locBody: loc.body,
      locBodyCleaned: cleanedBody,
      locAddress: (loc as { address?: string }).address,
      locNotes: loc.notes,
      displayTitle,
      displayAddress,
      displayNotes,
      displayTitleSource: explicitName ? 'name' : cleanedBody ? 'body' : 'fallback',
      displayAddressSource: (loc as { address?: string }).address ? 'address' : loc.location?.a3_intl_address ? 'location.a3_intl_address' : 'other',
    });
  }

  const placeSummary = getPlaceSummaryFromLocation(loc.location);

  return {
    id:
      loc._id ||
      loc.location_id ||
      `${loc.itinerary_day_index ?? 0}-${loc.itinerary_day_order_index ?? 0}`,
    title: displayTitle,
    slotType: resolvedSlotType,
    notes: displayNotes,
    placeSummary: placeSummary?.text,
    placeSummaryIsAi: placeSummary?.isAi,
    timeRange,
    address: displayAddress,
    cost: costValue,
    imageUrl, // Legacy: first photo (for backward compatibility)
    imageUrls: uniquePhotoUrls, // Array of all photos (for carousel)
    recommendations: recommendationsCount,
    travellerTypes,
    phone: loc.location?.phone || locationData.phone,
    website: loc.location?.website || locationData.website,
    locationName: loc.location?.name || locationData.name,
    locationId:
      loc.location?._id ||
      loc.location_id ||
      locationData._id ||
      undefined,
    placeId:
      loc.location?.google_place_id ||
      loc.location?.google_places_data?.id ||
      loc.placeId,
    locationData,
    coordinates: coordinates
      ? { lat: coordinates[1], lng: coordinates[0] }
      : undefined,
  };
};

const normalizeAuthor = (
  post: RawItineraryPost,
  options?: NormalizeOptions
): PostPresentationAuthor => {
  const author = post.user || {};
  const avatar =
    author.large_avatar_url ||
    author.avatar_url ||
    author.profile_image_url ||
    post.user_avatar ||
    null;

  const currentUserId = options?.currentUserId;
  const authorId = author._id || author.id;

  return {
    id: authorId,
    name: author.name || author.username,
    avatarUrl: avatar || undefined,
    isCurrentUserAuthor:
      !!authorId && !!currentUserId ? authorId === currentUserId : undefined,
  };
};

const normalizeMetadata = (
  post: RawItineraryPost
): PostPresentationMetadata => {
  const city =
    post.primary_destination?.identifying_name ||
    post.city_identifying_names?.[0] ||
    post.location?.city_identifying_name ||
    post.location?.city ||
    undefined;

  const itDataDays = post.itinerary_data?.days;
  const durationDays =
    (Array.isArray(itDataDays) ? itDataDays.length : 0) ||
    (typeof itDataDays === 'number' && Number.isFinite(itDataDays) ? itDataDays : 0) ||
    (post.itinerary_data as { day_count?: number } | undefined)?.day_count ||
    (Array.isArray(post.post_locations)
      ? new Set(
          post.post_locations.map((loc) =>
            typeof loc.itinerary_day_index === "number"
              ? loc.itinerary_day_index
              : 0
          )
        ).size || 0
      : 0);

  const vibe =
    post.vibe_description ||
    post.itinerary_data?.vibe_description ||
    undefined;

  return {
    durationDays: durationDays || 0,
    vibeDescription: vibe,
    city,
    tags: post.tag_list || undefined,
    isDraft: Boolean(post.is_draft),
    isPrivate: Boolean(post.is_private),
  };
};

const parseDateCandidate = (
  value: unknown,
  seen: WeakSet<object> = new WeakSet(),
  depth = 0
): Date | null => {
  if (depth > 10) return null;
  if (!value) return null;
  if (isRecord(value)) {
    const objectValue = value as object;
    if (seen.has(objectValue)) {
      return null;
    }
    seen.add(objectValue);
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "string") {
    // Parse YYYY-MM-DD strings in local timezone (not UTC) to avoid timezone shifts
    // This matches the parseLocalDate approach used in edit mode
    if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
      // YYYY-MM-DD format - parse in local timezone
      const [year, month, day] = value.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return Number.isNaN(date.getTime()) ? null : date;
    }
    // For other date string formats, use native Date parsing
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (
    isRecord(value) &&
    ("$date" in value ||
      "iso" in value ||
      "value" in value ||
      "date" in value ||
      "startDate" in value ||
      "calendar_date" in value)
  ) {
    const candidate =
      value["$date"] ??
      value["iso"] ??
      value["value"] ??
      value["date"] ??
      value["startDate"] ??
      value["calendar_date"];
    return parseDateCandidate(candidate, seen, depth + 1);
  }
  return null;
};

const formatDate = (date: Date): string =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const getDayDateLabel = (
  dayIndex: number,
  dayInfo: RawItineraryDayInfo | undefined,
  loc: RawItineraryPostLocation,
  itineraryStartDate: Date | null
): string | null => {
  if (dayInfo) {
    const candidates: unknown[] = [
      dayInfo.date,
      dayInfo.date_string,
      dayInfo.start_date,
      dayInfo.calendar_date,
      dayInfo.formattedDate,
      dayInfo.formatted_date,
      dayInfo.label,
      dayInfo.title,
      dayInfo.display_date,
      dayInfo.displayDate,
      dayInfo.timeline_date,
      dayInfo.timelineDate,
      dayInfo.day_date,
      dayInfo.dayDate,
      loc.itinerary_day_date,
      loc.itinerary_day_date_string,
      loc.itinerary_date,
      loc.itinerary_date_string,
      loc.calendar_date,
    ];
    for (const candidate of candidates) {
      const parsed = parseDateCandidate(candidate);
      if (parsed) {
        return formatDate(parsed);
      }
      if (typeof candidate === "string" && candidate.trim().length > 0) {
        return candidate.trim();
      }
    }
  }

  if (itineraryStartDate) {
    const computed = new Date(itineraryStartDate.getTime());
    computed.setDate(computed.getDate() + dayIndex);
    return formatDate(computed);
  }

  return null;
};

const collectSourceLocations = (
  post: RawItineraryPost
): RawItineraryPostLocation[] => {
  let locations: (RawItineraryPostLocation | Record<string, unknown>)[] = [];
  
  if (Array.isArray(post.post_locations) && post.post_locations.length) {
    locations = post.post_locations;
  } else if (Array.isArray(post.postLocations) && post.postLocations.length) {
    locations = post.postLocations;
  }
  const isItinerary = post.post_type !== PostType.List && post.post_type !== PostType.Place;
  if (isItinerary && locations.length > 1) {
    locations = dedupeItinerarySlotsByPlace(locations);
  }
  
  if (locations.length === 0) {
    return [];
  }

  debug('[itineraryPresentationNormalizer] collectSourceLocations - slot type info:', {
    locationsCount: locations.length,
    firstLocationType: locations[0]?.constructor?.name,
    firstLocationHasGtCategory: !!(locations[0] as Record<string, unknown>)?.['gt_category'],
    firstLocationHasSlotType: !!(locations[0] as Record<string, unknown>)?.['slotType'],
    firstLocationGtCategory: (locations[0] as Record<string, unknown>)?.['gt_category'],
    firstLocationSlotType: (locations[0] as Record<string, unknown>)?.['slotType'],
  });

  // Convert PostLocation class instances to plain objects to ensure all fields are accessible
  const converted: RawItineraryPostLocation[] = locations.map((loc, idx: number) => {
    const typedLoc = loc as RawItineraryPostLocation | Record<string, unknown>;
    
    // Check if this is a PostLocation class instance
    if (typedLoc && typeof typedLoc === 'object' && 'constructor' in typedLoc && typedLoc.constructor?.name === 'PostLocation') {
      // Extract all fields from PostLocation instance, including address and is_user_created for user-created places
      const rawLoc = typedLoc as Record<string, unknown>;
      const originalIsUserCreated = rawLoc['is_user_created'] as boolean | undefined;
      const originalIsUserCreatedCamel = rawLoc['isUserCreated'] as boolean | undefined;
      const isUserCreatedLoc = originalIsUserCreated || originalIsUserCreatedCamel;
      
      const plainLoc: RawItineraryPostLocation = {
        _id: rawLoc['_id'] as string | undefined,
        location_id: rawLoc['location_id'] as string | undefined,
        body: rawLoc['body'] as string | undefined,
        address: rawLoc['address'] as string | undefined, // User-entered address for user-created places
        itinerary_day_index: rawLoc['itinerary_day_index'] as number | undefined,
        itinerary_day_order_index: rawLoc['itinerary_day_order_index'] as number | undefined,
        image_urls: rawLoc['image_urls'] as string[] | undefined,
        location: rawLoc['location'] as import('@/utils/postTypes').RawLocationData | undefined,
        // Extract new fields
        gt_category: rawLoc['gt_category'] as string | undefined,
        slotType: rawLoc['slotType'] as string | undefined,
        notes: rawLoc['notes'] as string | undefined,
        budget: rawLoc['budget'] as number | string | null | undefined,
        timeRange: (rawLoc['timeRange'] as string | undefined) || (rawLoc['time'] as string | undefined),
        // Handle both snake_case and camelCase from backend/database
        is_user_created: originalIsUserCreated ?? originalIsUserCreatedCamel ?? false,
        isUserCreated: originalIsUserCreatedCamel ?? originalIsUserCreated ?? false,
      };
      
      // Log conversion for user-created places OR first 3 items
      if (idx < 3 || isUserCreatedLoc) {
        debug('[itineraryPresentationNormalizer] Converted PostLocation instance to plain object', {
          index: idx,
          postLocationId: rawLoc['_id'],
          originalGtCategory: rawLoc['gt_category'],
          originalSlotType: rawLoc['slotType'],
          originalBody: rawLoc['body'],
          originalBodyType: typeof rawLoc['body'],
          originalAddress: rawLoc['address'],
          originalAddressType: typeof rawLoc['address'],
          originalIsUserCreated,
          originalIsUserCreatedCamel,
          convertedGtCategory: plainLoc.gt_category,
          convertedSlotType: plainLoc.slotType,
          convertedBody: plainLoc.body,
          convertedBodyType: typeof plainLoc.body,
          convertedAddress: plainLoc.address,
          convertedAddressType: typeof plainLoc.address,
          convertedIsUserCreated: plainLoc.is_user_created,
          convertedIsUserCreatedCamel: plainLoc.isUserCreated,
          isUserCreatedLoc,
        });
      }
      
      return plainLoc;
    }
    
    // Already a plain object, use as-is
    return typedLoc as RawItineraryPostLocation;
  });

  debug('[itineraryPresentationNormalizer] collectSourceLocations - After conversion:', {
    convertedCount: converted.length,
    sampleGtCategories: converted.slice(0, 3).map(loc => ({
      _id: loc._id,
      gt_category: loc.gt_category,
      slotType: loc.slotType,
    })),
  });

  return converted;
};

export const derivePresentationDays = (
  post: RawItineraryPost
): PostPresentationDay[] => {
  const isListPost = post.post_type === PostType.List || post.post_type === PostType.Place;
  
  debug('[postPresentationNormalizer] derivePresentationDays - Starting', {
    postId: post._id || post.id,
    post_type: post.post_type,
    hasItineraryData: !!post.itinerary_data,
    itineraryDataKeys: post.itinerary_data ? Object.keys(post.itinerary_data) : [],
  });
  
  const rawDays = post.itinerary_data?.days;
  const itineraryDaysInfo = Array.isArray(rawDays) ? rawDays : [];

  const itineraryStartDate =
    parseDateCandidate(post.itinerary_data?.start_date_string) ||
    parseDateCandidate(post.itinerary_data?.start_date) ||
    parseDateCandidate(post.start_date_string) ||
    parseDateCandidate(post.start_date);

  const sourceLocations = collectSourceLocations(post);

  // Extract day_notes from itinerary_data.day_notes
  // Backend always returns day_notes as array: [{ day_index: 0, notes: "..." }, ...]
  // Can be null, undefined, or [] if no notes exist
  // Check raw_itinerary_data first (if post is a Post instance with preserved raw data),
  // otherwise check itinerary_data directly
  const dayNotesFromItinerary: Record<number, string> = {};
  const rawItineraryData = (post as Record<string, unknown>)['raw_itinerary_data'] as typeof post.itinerary_data | undefined || post.itinerary_data;
  const itineraryDayNotesRaw = (rawItineraryData as { day_notes?: Array<{ day_index: number; notes: string }> } | undefined)?.day_notes;
  const itineraryDayNotes = itineraryDayNotesRaw || [];
  
  debug('[postPresentationNormalizer] derivePresentationDays - Day notes extraction', {
    postId: post._id || post.id,
    day_notes_raw: itineraryDayNotesRaw,
    day_notes_type: Array.isArray(itineraryDayNotesRaw) ? 'Array' : typeof itineraryDayNotesRaw,
    day_notes_array_length: Array.isArray(itineraryDayNotesRaw) ? itineraryDayNotesRaw.length : 'N/A',
    day_notes_after_default: itineraryDayNotes,
    day_notes_after_default_length: Array.isArray(itineraryDayNotes) ? itineraryDayNotes.length : 'N/A',
  });
  
  // Convert array to map for easy lookup: { 0: "notes", 1: "notes" }
  if (Array.isArray(itineraryDayNotes)) {
    itineraryDayNotes.forEach((dayNote: { day_index?: number; notes?: string }) => {
      const dayIndex = dayNote.day_index;
      const notes = dayNote.notes;
      // Only include if day_index is valid number and notes is a non-empty string
      if (typeof dayIndex === 'number' && dayIndex >= 0 && notes && typeof notes === 'string' && notes.trim()) {
        dayNotesFromItinerary[dayIndex] = notes.trim();
      }
    });
  }
  
  debug('[postPresentationNormalizer] derivePresentationDays - Day notes map created', {
    postId: post._id || post.id,
    dayNotesMap: dayNotesFromItinerary,
    dayNotesMapKeys: Object.keys(dayNotesFromItinerary),
    dayNotesMapCount: Object.keys(dayNotesFromItinerary).length,
  });

  // For List posts, group all locations into a single "day" (index 0)
  // For Itinerary posts, use the actual itinerary_day_index
  const sortedLocations = sourceLocations.sort((a, b) => {
    if (isListPost) {
      // For Lists, just sort by order index (or sequence if available)
      return (a.itinerary_day_order_index ?? a.sequenceIndex ?? 0) - (b.itinerary_day_order_index ?? b.sequenceIndex ?? 0);
    }
    const dayA = a.itinerary_day_index ?? 0;
    const dayB = b.itinerary_day_index ?? 0;
    if (dayA !== dayB) return dayA - dayB;
    return (a.itinerary_day_order_index ?? 0) - (b.itinerary_day_order_index ?? 0);
  });

  const daysMap = new Map<number, PostPresentationDay>();

  sortedLocations.forEach((loc) => {
    // For List posts, always use dayIndex 0; for Itineraries, use the actual day index
    const dayIndex = isListPost ? 0 : (loc.itinerary_day_index ?? 0);
    // Unscheduled stops (negative day index) belong to no day; the trip editor lists them separately.
    if (isUnscheduledDayIndex(dayIndex)) return;
    const existing = daysMap.get(dayIndex);
    const activity = buildActivity(loc);
    const notes =
      loc.day_label || loc.day_notes || loc.travelDay || loc.dayNote || null;
    const dayInfo = itineraryDaysInfo[dayIndex];
    const dayDateLabel = getDayDateLabel(
      dayIndex,
      dayInfo,
      loc,
      itineraryStartDate
    );

    if (!existing) {
      // For List posts, use "Places" as the label instead of "Day 1"
      const dayLabel = isListPost
        ? 'Places'
        : dayDateLabel ||
          loc.day_label ||
          loc.travelDay ||
          `Day ${dayIndex + 1}`;

      // Prefer day_notes from itinerary_data over location-level notes
      const dayNotes = dayNotesFromItinerary[dayIndex] || notes;

      daysMap.set(dayIndex, {
        index: dayIndex,
        label: dayLabel,
        date: dayDateLabel,
        notes: dayNotes || null,
        places: [activity],
      });
      return;
    }

    existing.places.push(activity);
    // Prefer day_notes from itinerary_data over location-level notes
    // Always update with day_notes from itinerary_data if available
    if (dayNotesFromItinerary[dayIndex]) {
      existing.notes = dayNotesFromItinerary[dayIndex];
    } else if (!existing.notes) {
      existing.notes = notes || null;
    }
    if (!existing.date && dayDateLabel) {
      existing.date = dayDateLabel;
      existing.label = dayDateLabel;
    }
  });

  // Resolve how many days this itinerary should show.
  // For List/Place posts, skip scaffolding — lists always have a single implicit "day".
  // For Itinerary posts, resolve from multiple sources so newly-created empty trips
  // (which have no post_locations yet) still render at least one editable day.
  const resolvedDayCount = (() => {
    if (isListPost) return daysMap.size;
    const explicitDayCount = (post.itinerary_data as { day_count?: number } | undefined)?.day_count;
    const numericDays = typeof rawDays === 'number' && Number.isFinite(rawDays) && rawDays >= 1
      ? rawDays
      : undefined;
    const maxDayIndexFromLocations = daysMap.size > 0
      ? Math.max(...Array.from(daysMap.keys())) + 1
      : 0;
    const count = Math.max(
      itineraryDaysInfo.length,
      numericDays ?? 0,
      explicitDayCount ?? 0,
      maxDayIndexFromLocations,
    );
    return count >= 1 ? count : 1;
  })();

  for (let i = 0; i < resolvedDayCount; i++) {
    if (!daysMap.has(i)) {
      const emptyLoc: RawItineraryPostLocation = {};
      const dayDateLabel = getDayDateLabel(i, itineraryDaysInfo[i], emptyLoc, itineraryStartDate);
      const dayLabel = isListPost
        ? 'Places'
        : dayDateLabel || `Day ${i + 1}`;
      daysMap.set(i, {
        index: i,
        label: dayLabel,
        date: dayDateLabel || null,
        notes: dayNotesFromItinerary[i] || null,
        places: [],
      });
    } else {
      const day = daysMap.get(i)!;
      if (dayNotesFromItinerary[i]) {
        day.notes = dayNotesFromItinerary[i];
      }
    }
  }

  const resultDays = Array.from(daysMap.values()).map((day) => ({
    ...day,
    places: [...day.places],
  }));
  
  debug('[postPresentationNormalizer] derivePresentationDays - Final result', {
    postId: post._id || post.id,
    daysCount: resultDays.length,
    daysWithNotes: resultDays.filter(d => d.notes).map(d => ({
      index: d.index,
      label: d.label,
      notes: d.notes,
      hasNotes: !!d.notes,
    })),
    allDaysNotes: resultDays.map(d => ({
      index: d.index,
      label: d.label,
      notes: d.notes || '(null/empty)',
    })),
  });

  return resultDays;
};

export const normalizeItineraryForPresentation = (
  post: RawItineraryPost,
  options?: NormalizeOptions
): PostPresentationData => {
  const days = derivePresentationDays(post);

  const mapLocations: PostLocationExtended[] = [];

  days.forEach((day) => {
    day.places.forEach((activity, activityIndex) => {
      const coordinates = activity.coordinates;
      const locationData = activity.locationData;

      if (!coordinates || !locationData) {
        return;
      }

      // Prefer activity.imageUrls (includes user-uploaded S3 from loc.image_urls); fallback to Google photos
      const googlePhotoUrls =
        locationData.photos?.map((photo) => {
          if (photo?.name && typeof photo.name === 'string') {
            return getGooglePhotoProxyUrlFromName(photo.name, 400);
          }
          if (photo?.photo_reference && typeof photo.photo_reference === 'string') {
            return getGooglePhotoProxyUrl(photo.photo_reference, 400);
          }
          return '';
        }).filter((url) => url) || [];
      const activityImageUrls = (activity as { imageUrls?: string[] }).imageUrls;
      const locationImageUrls = (locationData as { image_urls?: string[] }).image_urls;
      const imageUrls =
        (Array.isArray(activityImageUrls) && activityImageUrls.length > 0)
          ? activityImageUrls
          : (Array.isArray(locationImageUrls) && locationImageUrls.length > 0)
            ? locationImageUrls
            : googlePhotoUrls;

      mapLocations.push({
        _id: activity.id || `itinerary-${day.index}-${activityIndex}`,
        body: (activity as { title?: string }).title ?? "",
        created_at: new Date().toISOString(),
        image_urls: imageUrls,
        itinerary_day_index: day.index,
        itinerary_day_order_index: activityIndex,
        location_id:
          activity.locationId ||
          activity.placeId ||
          locationData._id ||
          `${coordinates.lng}-${coordinates.lat}`,
        post_id: post?._id || "",
        published_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        location: {
          ...locationData,
          geoposition: {
            type: "Point",
            coordinates: [coordinates.lng, coordinates.lat],
          },
        },
        gt_category: locationData?.gt_category,
        isActive: true,
        forceOpen: false,
        title: activity.title,
        address: activity.address,
        dayIndex: day.index,
        sequenceIndex: activityIndex,
        displayNumber: String(activityIndex + 1),
        pinColorByDayIndex: true,
      } as PostLocationExtended);
    });
  });

  const primaryDestCoords = getPrimaryDestinationCoordinates(post?.primary_destination);
  const defaultCenter: { lat: number; lng: number } | undefined =
    primaryDestCoords ??
    (mapLocations[0]?.location?.geoposition?.coordinates?.length === 2
      ? {
          lng: mapLocations[0].location.geoposition.coordinates[0],
          lat: mapLocations[0].location.geoposition.coordinates[1],
        }
      : undefined);

  // Extract image URLs array for cover photos
  const imageUrls: string[] = (post?.image_urls || [])
    .map((img) => {
      if (typeof img === 'string') return img;
      return (img as { url?: string })?.url || '';
    })
    .filter((url) => Boolean(url));

  return {
    id: post?._id || "",
    name: post?.name,
    description: post?.body,
    heroImage:
      post?.cover_image_url ||
      (typeof post?.image_urls?.[0] === 'string'
        ? post.image_urls[0]
        : post?.image_urls?.[0]?.url) ||
      null,
    imageUrls, // Include full array for carousel
    author: normalizeAuthor(post, options),
    metadata: normalizeMetadata(post),
    map: {
      defaultCenter,
      postLocations: mapLocations,
    },
    days,
    post_type: post?.post_type as PostTypeValue | undefined,  // Preserve post type for List detection
    // Preserve save data from Post model
    // Check if current user has saved it by looking at saves array
    isSaved: options?.currentUserId
      ? (() => {
          const p = post as Record<string, unknown> & {
            isSavedByUser?: (id: string) => boolean;
          };
          if (typeof p.isSavedByUser === 'function') {
            const r = p.isSavedByUser(options.currentUserId);
            if (typeof r === 'boolean') return r;
          }
          const savesRaw = p['saves'];
          const savesArr = Array.isArray(savesRaw) ? savesRaw : [];
          return savesArr.some((save: unknown) => {
            if (!save || typeof save !== 'object') return false;
            const s = save as Record<string, unknown>;
            const saveUserId = s.user_id ?? s.userId;
            return String(saveUserId) === String(options.currentUserId);
          });
        })()
      : Boolean((post as Record<string, unknown>)['is_saved']),
    saveCount: Array.isArray((post as Record<string, unknown>)?.['saves']) ? ((post as Record<string, unknown>)['saves'] as unknown[]).length : 0,
  };
};


